import { GlobalSearchTabPanelProps } from './GlobalSearchTabPanelTypes';
import { fetchUnifiedSearch } from '@/lib/api';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  PartnerSchema,
  PartnerType,
  UnifiedSearchResponse,
} from '@/lib/types';
import Table from '@/components/Table/Table';
import { ColumnDef } from '@/components/Table/TableHelpers';
import PartnerBadge from '@/components/PartnerBadge/PartnerBadge';
import CircularProgress from '@mui/material/CircularProgress';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import { styled as muiStyled } from '@mui/material/styles';
import { ga } from '@/lib/analytics';

type UnifiedSearchPartnersResponse = UnifiedSearchResponse & {
  partners?: {
    hits: PartnerSchema[];
  };
};

const normalizePartnerType = (
  value: unknown,
): PartnerType | undefined => {
  if (typeof value !== 'string') return undefined;
  return value.trim().toLowerCase() as PartnerType;
};

const PartnersSearchTabPanel = ({
  query,
  onResultNavigate,
  scrollContainerRef,
  onResultsLoaded,
}: GlobalSearchTabPanelProps) => {
  const navigate = useNavigate();
  const trimmedQuery = query.trim();
  const hasSufficientQuery = trimmedQuery.length >= 2;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    total_count,
  } = useInfiniteScroll<
    PartnerSchema,
    Record<string, unknown>,
    UnifiedSearchPartnersResponse
  >({
    section: 'Global Search Partners',
    fetchFn: ({ offset, limit }) =>
      fetchUnifiedSearch({
        q: trimmedQuery,
        ttype: ['partners'],
        offset,
        limit,
      }),
    queryKey: ['global-search', 'partners', trimmedQuery],
    scrollContainerRef,
    useWindowScroll: false,
    pageSize: 20,
    enabled: hasSufficientQuery,
    extractData: (response) =>
      (response.partners?.hits as PartnerSchema[]) ?? [],
  });

  useEffect(() => {
    if (!hasSufficientQuery || isLoading) return;
    onResultsLoaded?.({
      query: trimmedQuery,
      scope: 'partners',
      resultCount: total_count ?? data.length,
    });
  }, [
    data.length,
    hasSufficientQuery,
    isLoading,
    onResultsLoaded,
    total_count,
    trimmedQuery,
  ]);

  const handleResultClick = (partner: PartnerSchema) => {
    ga.trackSearchResultClick({
      result_type: 'partner',
      result_id: partner.id,
      position: data.findIndex((p) => p.id === partner.id) + 1,
      search_term: trimmedQuery,
    });
    onResultNavigate?.();
    navigate(`/admin/partners/${partner.id}`);
  };

  const columns: ColumnDef<PartnerSchema>[] = useMemo(
    () => [
      {
        field: 'name',
        label: 'Partner Name',
        render: (row, value) => (
          <PartnerNameLink
            role="link"
            tabIndex={0}
            onClick={() => handleResultClick(row)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleResultClick(row);
              }
            }}
          >
            {String(value ?? '')}
          </PartnerNameLink>
        ),
        width: '65%',
      },
      {
        field: 'type',
        label: 'Partner Type',
        render: (_row, value) => (
          <PartnerBadge type={normalizePartnerType(value)} />
        ),
        width: '35%',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, trimmedQuery],
  );

  return (
    <ScrollableTabContent className="wide-scrollbar">
      {data.length > 0 ? (
        <StyledTable rows={data} columns={columns} />
      ) : !isLoading && !isFetchingNextPage ? (
        <NoResultsText>
          <FormattedMessage
            id="noSearchResults"
            defaultMessage="No search results."
          />
        </NoResultsText>
      ) : null}
      {(isLoading || isFetchingNextPage) && (
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      )}
    </ScrollableTabContent>
  );
};

export default PartnersSearchTabPanel;

const ScrollableTabContent = styled('div')({});

const StyledTable = styled(Table<PartnerSchema>)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const PartnerNameLink = muiStyled('span')(({ theme }) => ({
  cursor: 'pointer',
  color: theme.palette.textPrimaryBlack,
  fontWeight: 600,
  '&:hover': {
    color: theme.palette.red600,
    textDecoration: 'underline',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.red600}`,
    outlineOffset: 2,
    borderRadius: 2,
  },
}));

const LoaderContainer = styled('div')({
  width: '100%',
  padding: '40px',
  display: 'flex',
  justifyContent: 'center',
});

const NoResultsText = styled('div')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(6),
  textAlign: 'center',
  color: theme.palette.textSecondaryDarkGrey,
}));
